import { Component, effect, inject, OnInit } from '@angular/core';
import { Table } from '../../components/table/table';
import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import {
  TableHeadSelectionComponent,
  TableRowSelectionComponent,
} from '../../components/select-column/select-column';
import { Select } from '../../components/select/select';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { HasRolesDirective } from 'keycloak-angular';
import { AdminService } from '../../services/admin-service';
import { TefBoxService } from '../../services/tef-box-service';
import Keycloak from 'keycloak-js';
import { Button } from '../../components/button/button';
import { Modal } from '../../components/modal/modal';
import { InputComponent } from '../../components/input/input';
import { TableButtons } from '../../components/table-buttons/table-buttons';
import { ConfirmDialogService } from '../../services/confirm-dialog-service';
import { ToastService } from '../../services/toast-service';
import { ConfirmDialog } from '../../components/confirm-dialog/confirm-dialog';
import { Loading } from '../../components/loading/loading';
import { ClienteService } from '../../services/cliente-service';
import { Terminal, TerminalButton } from './terminal/terminal';

type TCheckout = {
  checkoutId: string;
  checkoutLabel: string;
  merchantId: string;
};

@Component({
  selector: 'app-checkouts',
  imports: [
    Table,
    Select,
    ReactiveFormsModule,
    HasRolesDirective,
    Button,
    Modal,
    InputComponent,
    ConfirmDialog,
    Loading,
    Terminal,
  ],
  templateUrl: './checkouts.html',
  styleUrl: './checkouts.scss',
})
export class Checkouts implements OnInit {
  loading = false;

  columns: ColumnDef<TCheckout>[] = [
    {
      id: 'select-col',
      header: () => flexRenderComponent(TableHeadSelectionComponent),
      cell: () => flexRenderComponent(TableRowSelectionComponent),
    },
    {
      accessorKey: 'checkoutLabel',
      header: 'Checkout Label',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'checkoutId',
      header: 'Checkout ID',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'merchantId',
      header: 'Merchant ID',
      cell: (info) => info.getValue(),
    },
    {
      id: 'terminalsAction',
      enableHiding: false,
      cell: ({ row }) => {
        const checkout = row.original;
        return flexRenderComponent(TerminalButton, {
          inputs: {
            id: checkout.checkoutId,
          },
          outputs: {
            abrir: () => this.openTerminalModal(checkout.checkoutId, checkout.checkoutLabel),
          },
        });
      },
    },
    {
      id: 'deleteAction',
      enableHiding: false,
      cell: ({ row }) => {
        const checkout = row.original;
        return flexRenderComponent(TableButtons, {
          inputs: {
            title: 'trash',
          },
          outputs: {
            clickEvent: () => this.deletarCheckout(checkout),
          },
        });
      },
    },
  ];
  message = '';
  showConfirmDialog = false;
  data: any[] = [];

  showModal = false;

  showTerminalModal = false;
  selectedCheckout: string | null = null;
  selectedCheckoutLabel: string = '';

  merchantId = new FormControl('');
  clientId = new FormControl('');

  role = 'lojista';

  merchantOptions: any[] = [];

  checkoutLabel = new FormControl('', [Validators.required, Validators.minLength(3)]);

  clientOptions: any[] = [];

  constructor(
    private adminService: AdminService,
    private clienteService: ClienteService,
    private tefBoxService: TefBoxService,
    private confirmDialogService: ConfirmDialogService,
    private toast: ToastService
  ) {
    const keycloak = inject(Keycloak);

    this.confirmDialogService.showDialog$.subscribe(({ message }) => {
      this.message = message;
      this.showConfirmDialog = true;
    });

    this.merchantId.valueChanges.subscribe((value) => {
      this.getCheckouts();
    });
    this.clientId.valueChanges.subscribe((value) => {
      if (value != null) {
        this.getMerchants();
        this.merchantId.enable();
      } else {
        this.merchantId.disable();
      }
    });

    effect(() => {
      const clientRoles = keycloak.resourceAccess?.['tefbox-api-lojista'].roles || [];

      if (clientRoles.includes('admin')) {
        this.role = 'admin';
        this.getClients();
      }

      if (clientRoles.includes('cliente')) {
        this.role = 'cliente';
        this.merchantId.enable();
        this.getCheckouts();
        this.getMerchants();
      }
    });
  }

  ngOnInit(): void {
    this.getClients();
    if (this.role != 'cliente') this.merchantId.disable();
    if (this.role != 'admin') {
      this.getCheckouts();
    }
  }

  openTerminalModal(checkoutId: string, checkoutLabel: string) {
    this.selectedCheckout = checkoutId;
    this.selectedCheckoutLabel = checkoutLabel;
    this.showTerminalModal = true;
  }

  closeTerminalModal() {
    this.selectedCheckout = null;
    this.selectedCheckoutLabel = '';
    this.showTerminalModal = false;
  }

  onConfirm() {
    this.confirmDialogService.confirm();
    this.showConfirmDialog = false;
  }

  onCancel() {
    this.confirmDialogService.cancel();
    this.showConfirmDialog = false;
  }

  getCheckouts() {
    this.loading = true;

    switch (this.role) {
      case 'lojista':
        this.tefBoxService.getCheckouts().subscribe({
          next: (resp) => {
            this.data = resp || [];
          },
          error: () => {
            this.data = [];
          },
        });
        break;
      case 'cliente':
        this.clienteService.getCheckouts(this.merchantId.value!).subscribe({
          next: (resp) => {
            this.data = resp || [];
          },
          error: () => {
            this.data = [];
          },
        });
        break;
      case 'admin':
        this.adminService.getCheckouts(this.clientId.value!, this.merchantId.value!).subscribe({
          next: (resp) => {
            this.data = resp || [];
          },
          error: () => {
            this.data = [];
          },
        });
        break;
    }
    this.loading = false;
  }

  getMerchants() {
    if (this.role == 'admin') {
      this.adminService.getMerchants(this.clientId.value!).subscribe(
        (resp) =>
          (this.merchantOptions = resp.map((m) => ({
            label: m.merchantLabel,
            value: m.merchantId,
          })))
      );
    } else {
      this.clienteService.getMerchants().subscribe(
        (resp) =>
          (this.merchantOptions = resp.map((m) => ({
            label: m.merchantLabel,
            value: m.merchantId,
          })))
      );
    }
  }

  getClients() {
    if (this.role == 'admin') {
      this.adminService
        .getClients()
        .subscribe(
          (resp) =>
            (this.clientOptions = resp.map((c) => ({ label: c.clientLabel, value: c.clientId })))
        );
    }
  }

  closeModal() {
    this.showModal = false;
  }

  openModal() {
    this.showModal = true;
  }

  salvar() {
    if (this.checkoutLabel.valid) {
      if (this.role == 'cliente') {
        this.clienteService
          .registerCheckout(this.merchantId.value!, this.checkoutLabel.value!)
          .subscribe({
            next: () => {
              this.checkoutLabel.reset();
              this.closeModal();
              this.getCheckouts();
            },
            error: () => {
              this.toast.show({
                message: 'Selecione um estabelecimento',
                type: 'danger',
              });
            },
          });
      } else {
        this.tefBoxService.registerCheckout(this.checkoutLabel.value!).subscribe(() => {
          this.checkoutLabel.reset();
          this.closeModal();
          this.getCheckouts();
        });
      }
    }
  }

  deletarCheckout(checkout: TCheckout) {
    this.confirmDialogService
      .requestConfirmation(`Deseja checkout [${checkout.checkoutLabel}]?`)
      .subscribe((confirmed) => {
        if (confirmed) {
          this.tefBoxService.deletarCheckout(checkout.checkoutId).subscribe(() => {
            this.toast.show({
              message: 'Checkout deletado com sucesso',
              type: 'info',
            });

            this.getCheckouts();
          });
        } else {
          console.log('Ação cancelada');
        }
      });
  }
}
